import { useCallback, useMemo, useRef, useState } from 'react';
import { fetchPlanAvailabilityDetail, planDetailKey } from '@/lib/resourceRequestPlanner';
import type { DailyUtilisationSlot } from '@/services/api';
import type { PlanScenario } from '@/types/resourceRequest';

type DaysByResourceId = Record<string, DailyUtilisationSlot[]>;

const NOTHING_READ: Record<string, DailyUtilisationSlot[] | null> = {};
const NOTHING_LOADING: ReadonlySet<string> = new Set();

interface ScenarioDays {
  /** The per-day utilisation read so far for each resource. */
  days?: DaysByResourceId;
  /**
   * {@link days}, plus a stand-in for each other pool resource carrying its month
   * totals on the first of the month: enough to score a whole calendar month.
   */
  scoringDays?: DaysByResourceId;
}

export interface PlanResourceDays {
  daysFor(scenario: PlanScenario | undefined): DaysByResourceId | undefined;
  scoringDaysFor(scenario: PlanScenario | undefined): DaysByResourceId | undefined;
  /** Whether the resource's days have still to be read before it can be fitted. */
  needsDays(scenario: PlanScenario, resourceId: string): boolean;
  /**
   * Reads the resource's days for the scenario's plan, once per resource and
   * window, and resolves to the scenario's days including them.
   */
  loadDays(scenario: PlanScenario, resourceId: string): Promise<DaysByResourceId | undefined>;
  isLoading(scenario: PlanScenario, resourceId: string): boolean;
}

/**
 * The per-day utilisation of the resources a plan read only in total, read on
 * demand when a planner picks one. A plan read in full already has every
 * resource's days, so its scenarios are passed through unchanged.
 *
 * Reads are kept for the plan they were made for: a new `planVersion` starts
 * again from the plan's own days.
 */
export function usePlanResourceDays(scenarios: PlanScenario[], planVersion: number): PlanResourceDays {
  const [read, setRead] = useState<{ version: number; days: Record<string, DailyUtilisationSlot[] | null> }>(
    { version: planVersion, days: {} },
  );
  const [loading, setLoading] = useState<{ version: number; keys: ReadonlySet<string> }>(
    { version: planVersion, keys: new Set() },
  );
  const pending = useRef<{ version: number; reads: Map<string, Promise<DailyUtilisationSlot[] | null>> }>(
    { version: planVersion, reads: new Map() },
  );
  if (pending.current.version !== planVersion) pending.current = { version: planVersion, reads: new Map() };

  const readDays = read.version === planVersion ? read.days : NOTHING_READ;
  const loadingKeys = loading.version === planVersion ? loading.keys : NOTHING_LOADING;

  const byScenario = useMemo(() => {
    const result = new Map<string, ScenarioDays>();
    for (const scenario of scenarios) {
      const context = scenario.planAvailabilityContext;
      if (!context) {
        result.set(scenario.id, {
          days: scenario.resourceDailyUtilisation,
          scoringDays: scenario.resourceDailyUtilisation,
        });
        continue;
      }

      const days: DaysByResourceId = { ...scenario.resourceDailyUtilisation };
      const scoringDays: DaysByResourceId = {};
      for (const [id, months] of Object.entries(scenario.resourceMonthlyUtilisation ?? {})) {
        const fetched = readDays[planDetailKey(context, id)];
        if (fetched) days[id] = fetched;
        scoringDays[id] = months.map(month => ({
          date: `${month.month}-01`,
          capacityHours: month.capacityHours,
          bookedHours: month.bookedHours,
        }));
      }
      Object.assign(scoringDays, days);
      result.set(scenario.id, {
        days: Object.keys(days).length > 0 ? days : undefined,
        scoringDays: Object.keys(scoringDays).length > 0 ? scoringDays : undefined,
      });
    }
    return result;
  }, [scenarios, readDays]);

  const daysFor = useCallback(
    (scenario: PlanScenario | undefined) => (scenario ? byScenario.get(scenario.id)?.days : undefined),
    [byScenario],
  );
  const scoringDaysFor = useCallback(
    (scenario: PlanScenario | undefined) => (scenario ? byScenario.get(scenario.id)?.scoringDays : undefined),
    [byScenario],
  );

  const needsDays = useCallback((scenario: PlanScenario, resourceId: string) => {
    const context = scenario.planAvailabilityContext;
    if (!context) return false;
    if (scenario.resourceDailyUtilisation?.[resourceId]) return false;
    if (!scenario.resourceMonthlyUtilisation?.[resourceId]) return false;
    return !(planDetailKey(context, resourceId) in readDays);
  }, [readDays]);

  const loadDays = useCallback(async (scenario: PlanScenario, resourceId: string) => {
    const context = scenario.planAvailabilityContext;
    const known = byScenario.get(scenario.id)?.days;
    if (!context || !needsDays(scenario, resourceId)) return known;

    const key = planDetailKey(context, resourceId);
    const version = planVersion;
    let request = pending.current.reads.get(key);
    if (!request) {
      request = fetchPlanAvailabilityDetail(context, [Number(resourceId)])
        .then(results => {
          const days = results.find(r => String(r.resourceId) === resourceId)?.dailyUtilisation;
          return days && days.length > 0 ? days : null;
        });
      pending.current.reads.set(key, request);
      setLoading(prev => ({
        version,
        keys: new Set([...(prev.version === version ? prev.keys : []), key]),
      }));
    }

    const finish = () => setLoading(prev => {
      if (prev.version !== version || !prev.keys.has(key)) return prev;
      const keys = new Set(prev.keys);
      keys.delete(key);
      return { version, keys };
    });

    try {
      const days = await request;
      finish();
      setRead(prev => {
        const base = prev.version === version ? prev.days : {};
        return key in base ? prev : { version, days: { ...base, [key]: days } };
      });
      return days ? { ...known, [resourceId]: days } : known;
    } catch {
      // Left unread so it is tried again; the resource is fitted from what is known.
      if (pending.current.version === version) pending.current.reads.delete(key);
      finish();
      return known;
    }
  }, [byScenario, needsDays, planVersion]);

  const isLoading = useCallback((scenario: PlanScenario, resourceId: string) => {
    const context = scenario.planAvailabilityContext;
    return context ? loadingKeys.has(planDetailKey(context, resourceId)) : false;
  }, [loadingKeys]);

  return { daysFor, scoringDaysFor, needsDays, loadDays, isLoading };
}
